/**
 * 🧭 왼쪽 메뉴 묶음 접기·펼치기 (v2.327.0 · 개발요청 #40 김소라 「도구와 관리 외 그룹들도 접기 기능이 있으면」)
 *
 * 전에는 도구·관리(MenuGroup.collapsible)만 머리글로 접고 펼쳤다(v2.318.0). 이제 **모든 묶음**이 그렇다.
 *   · 처음 모습은 그대로 — collapsible 묶음(도구·관리 · 숨긴 메뉴)은 처음에 접힘, 나머지는 처음에 펼침. 아무것도 안 누르면 전과 같다.
 *   · 사람이 누른 것만 이 PC 브라우저에 적는다 — 펼친 것(OPEN · v2.318.0 부터 쓰던 열쇠 그대로)과 접은 것(CLOSED · 새 열쇠).
 *   · 지금 보고 있는 화면이 든 묶음:
 *       - 처음부터 접힌 묶음(손대지 않음)이면 저절로 펼쳐 보인다 — 전과 같다(도구 › 메일을 보고 있으면 메일·NAS 둘 다).
 *       - 사람이 접었으면 **그 화면 한 줄만** 남긴다 — 눌렀는데 아무 일도 안 일어나 보이지 않게 · 지금 어디 있는지는 보이게.
 *   App.tsx(사이드바)가 쓰고 scripts/check-menu.ts ⑫ 가 검사한다.
 */
import type { MenuItem } from './menu';

/** 사람이 펼쳐 둔 머리글 (v2.318.0 부터 — 도구·관리·숨긴 메뉴) */
export const MENU_GROUPS_OPEN_KEY = 'sm-menu-groups-open';
/** 사람이 접어 둔 머리글 (v2.327.0 — 처음에 펼친 묶음을 접은 것 · 처음에 접힌 묶음을 보고 있는 화면째 접은 것) */
export const MENU_GROUPS_CLOSED_KEY = 'sm-menu-groups-closed';

export interface GroupOpenState {
  open: Set<string>;
  closed: Set<string>;
}

function readSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function readGroupOpenState(): GroupOpenState {
  return { open: readSet(MENU_GROUPS_OPEN_KEY), closed: readSet(MENU_GROUPS_CLOSED_KEY) };
}

export function writeGroupOpenState(s: GroupOpenState): void {
  try {
    localStorage.setItem(MENU_GROUPS_OPEN_KEY, JSON.stringify([...s.open]));
    localStorage.setItem(MENU_GROUPS_CLOSED_KEY, JSON.stringify([...s.closed]));
  } catch { /* 저장 못 해도 화면은 동작 */ }
}

export interface GroupView {
  /** 머리글 화살표 — 묶음 전체가 보이면 true(▲) */
  expanded: boolean;
  /** 머리글 아래에 그릴 메뉴 */
  items: MenuItem[];
}

/**
 * 묶음 하나를 어떻게 그릴지.
 *   defaultClosed — 처음에 접힌 묶음인가(collapsible · 숨긴 메뉴)
 *   isActive      — 그 메뉴가 지금 보고 있는 화면인가
 */
export function groupView(
  heading: string,
  defaultClosed: boolean,
  items: MenuItem[],
  state: GroupOpenState,
  isActive: (r: MenuItem) => boolean,
): GroupView {
  const userOpen = state.open.has(heading);
  const userClosed = state.closed.has(heading);
  const open = defaultClosed ? userOpen && !userClosed : !userClosed;
  if (open) return { expanded: true, items };
  const active = items.filter(isActive);
  if (active.length === 0) return { expanded: false, items: [] };
  // 처음부터 접힌 묶음(손대지 않음)은 전처럼 통째로 펼쳐 보인다 · 사람이 접었으면 보고 있는 화면 한 줄만
  if (defaultClosed && !userClosed) return { expanded: true, items };
  return { expanded: false, items: active };
}

/** 머리글을 눌렀을 때 — 지금 보이는 모양(▲/▼)의 반대로 적는다 */
export function toggleGroupState(heading: string, expandedNow: boolean, state: GroupOpenState): GroupOpenState {
  const open = new Set(state.open);
  const closed = new Set(state.closed);
  if (expandedNow) {
    open.delete(heading);
    closed.add(heading);
  } else {
    closed.delete(heading);
    open.add(heading);
  }
  return { open, closed };
}
